/**
 * services/hire/candidateV2.ts — client for the sk-hire v2 candidates API.
 */
import axiosInstance from '../../api/axiosInstance';
import {
  HireMatch_Candidates_Url,
  HireMatch_Candidate_Url,
  HireMatch_Candidate_Resumes_Url,
  HireMatch_Candidate_Profile_Url,
  HireMatch_Candidate_Consent_Url,
  HireMatch_Candidate_DismissDuplicate_Url,
  HireMatch_Candidate_ExtractFile_Url,
  HireMatch_Candidate_ExtractText_Url,
  HireMatch_Candidate_Lookup_Url,
  HireMatch_Candidate_SourceFile_Url,
  HireMatch_Candidate_SourceFileDownload_Url,
} from '../../api/endpoints';
import type {
  CandidateListItem,
  CandidateListResult,
  CandidateDetail,
  CreateCandidateInput,
  CreateCandidateResult,
  UpdateCandidateInput,
  ExtractCandidateFileResult,
  ExtractCandidateTextResult,
} from '../../schemas/hire/candidateV2';

const EMPTY_CAND_RESULT: CandidateListResult = {
  items: [], page: 1, pageSize: 10, total: 0,
  counts: { total: 0, active: 0, consent_expired: 0 },
};

export interface ListCandidatesParams {
  q?: string;
  page?: number;
  pageSize?: number;
}

/**
 * List candidates (paginated). The server applies the relevance search (`q`)
 * and pagination, returning the page plus aggregate counts. An optional
 * AbortSignal cancels a stale request while typing/paging.
 */
export async function listCandidates(params: ListCandidatesParams = {}, signal?: AbortSignal): Promise<CandidateListResult> {
  const term = params.q?.trim();
  const { data } = await axiosInstance.get<CandidateListResult>(HireMatch_Candidates_Url, {
    params: {
      ...(term ? { q: term } : {}),
      ...(params.page ? { page: params.page } : {}),
      ...(params.pageSize ? { pageSize: params.pageSize } : {}),
    },
    signal,
  });
  return data && Array.isArray(data.items) ? data : EMPTY_CAND_RESULT;
}

/** A candidate already holding this email/phone, if any. */
export interface CandidateLookupHit {
  id: string;
  code: string;
  name: string;
  status: string;
  createdAt: string;
}

/**
 * Is this contact already on file? Intake asks BEFORE adding: a repeat no longer
 * blocks — the create succeeds and flags the new record as a possible duplicate —
 * so the decision belongs to the recruiter, not to a 409.
 */
export async function lookupCandidateByContact(
  email?: string | null, phone?: string | null,
): Promise<CandidateLookupHit | null> {
  if (!email && !phone) return null;
  const { data } = await axiosInstance.get<{ match: CandidateLookupHit | null }>(
    HireMatch_Candidate_Lookup_Url,
    { params: { ...(email ? { email } : {}), ...(phone ? { phone } : {}) } },
  );
  return data?.match ?? null;
}

/** Fields of the CURRENT profile that a recruiter may correct in place. */
export interface UpdateProfileInput {
  skills?: { slug: string; years: number }[];
  totalExperienceYears?: number | null;
  currentLocation?: string | null;
  noticePeriodDays?: number | null;
  expectedSalary?: number | null;
  remoteOk?: boolean;
  summaryCompressed?: string | null;
}

/**
 * Correct the current extracted profile IN PLACE — no new resume version. Use
 * this to fix a bad extraction; use addResume when there is genuinely a newer
 * resume. The server keeps the lexical text, skill_id rows and embedding in step,
 * so matching reflects the corrected values.
 */
export async function updateCandidateProfile(
  id: string, input: UpdateProfileInput,
): Promise<CandidateDetail> {
  const { data } = await axiosInstance.patch<CandidateDetail>(HireMatch_Candidate_Profile_Url(id), input);
  return data;
}

export async function getCandidate(id: string): Promise<CandidateDetail> {
  const { data } = await axiosInstance.get<CandidateDetail>(HireMatch_Candidate_Url(id));
  return data;
}

export async function createCandidate(input: CreateCandidateInput): Promise<CreateCandidateResult> {
  const { data } = await axiosInstance.post<CreateCandidateResult>(HireMatch_Candidates_Url, input);
  return data;
}

/**
 * Append a NEW resume version and re-extract the profile from it. Pass
 * `resumeFileId` (the SK-Drive id returned by extractResumeFile) when the text
 * came from an uploaded document, so the version points at the stored original
 * instead of the 'inline' default.
 */
export async function addResume(
  id: string, resumeText: string, resumeFileId?: string | null,
): Promise<CandidateDetail> {
  const { data } = await axiosInstance.post<CandidateDetail>(
    HireMatch_Candidate_Resumes_Url(id),
    { resumeText, ...(resumeFileId ? { resumeFileId } : {}) },
  );
  return data;
}

/** Correct a candidate's contact details (name/email/phone). */
export async function updateCandidate(id: string, input: UpdateCandidateInput): Promise<CandidateDetail> {
  const { data } = await axiosInstance.patch<CandidateDetail>(HireMatch_Candidate_Url(id), input);
  return data;
}

export async function recordConsent(
  id: string, action: 'renew' | 'withdraw', opts?: { purpose?: string; ttlDays?: number },
): Promise<CandidateDetail> {
  const { data } = await axiosInstance.post<CandidateDetail>(HireMatch_Candidate_Consent_Url(id), { action, ...opts });
  return data;
}

/** CC-002 §16 — dismiss the advisory duplicate flag ("Not a duplicate"). */
export async function dismissDuplicate(id: string): Promise<CandidateDetail> {
  const { data } = await axiosInstance.post<CandidateDetail>(HireMatch_Candidate_DismissDuplicate_Url(id), {});
  return data;
}

/** Upload a resume (PDF/CSV/Excel): server parses → AI-scrapes contact → stores
 *  the original in SK-Drive. Returns scraped fields + parsed text + Drive id. */
export async function extractResumeFile(
  input: { filename: string; contentBase64: string; contentType: string },
  source?: 'bulk_import',
): Promise<ExtractCandidateFileResult> {
  const { data } = await axiosInstance.post<ExtractCandidateFileResult>(
    HireMatch_Candidate_ExtractFile_Url, input, source ? { params: { source } } : undefined,
  );
  return data;
}

/**
 * Scrape name / email / phone out of PASTED resume text (the paste twin of
 * extractResumeFile). Nothing is stored in SK-Drive — there is no uploaded
 * original — so the form keeps whatever source-file state it already had.
 */
export async function extractResumeText(resumeText: string): Promise<ExtractCandidateTextResult> {
  const { data } = await axiosInstance.post<ExtractCandidateTextResult>(
    HireMatch_Candidate_ExtractText_Url, { resumeText },
  );
  return data;
}

/** Signed download URL for a candidate's stored original resume (scan-gated server-side). */
export async function getCandidateSourceFile(id: string): Promise<{ url: string; filename: string | null }> {
  const { data } = await axiosInstance.get<{ url: string; filename: string | null }>(HireMatch_Candidate_SourceFile_Url(id));
  return data;
}

/** The resume BYTES, streamed same-origin as an attachment so the browser saves
 *  the file instead of rendering the PDF in a tab. */
export async function downloadCandidateSourceFile(id: string): Promise<Blob> {
  const { data } = await axiosInstance.get<Blob>(HireMatch_Candidate_SourceFileDownload_Url(id), { responseType: 'blob' });
  return data;
}
